<template>
  <v-form ref="form">
    <v-container>
      <text-field
        :required="true"
        type="text"
        v-model="localField.label"
        :rules="$validator.rules.required('Field Label')"
        @keydown.enter.stop
        data-testid="custom-fields-form-field-label-input"
        :label="$t('customFields.forms.customFieldsForm.labels.fieldLabel')"></text-field>

      <v-text-field
        type="text"
        v-model="localField.description"
        :label="$t('forms.common.labels.description')"
        data-testid="custom-fields-form-description-input"
        @keydown.enter.stop></v-text-field>

      <v-text-field
        v-if="!typesWithoutPlaceholder.includes(localField.type)"
        type="text"
        v-model="localField.placeholder"
        :label="$t('customFields.forms.customFieldsForm.labels.exampleValue')"
        data-testid="custom-fields-form-example-value-input"
        @keydown.enter.stop></v-text-field>
      <v-select
        v-model="localField.type"
        class="document-type-select"
        :items="fieldTypeOptions"
        data-testid="custom-fields-form-field-type-select"
        :rules="$validator.rules.required('Field Type')"
        :label="$t('customFields.forms.customFieldsForm.labels.fieldType')">
        <template #item="{ item, props }">
          <v-list-item v-bind="props" :title="null">
            <span v-html="item.raw.text"></span>
          </v-list-item>
        </template>
        <template #selection="{ item }">
          <span v-html="item.raw.text"></span>
        </template>
      </v-select>
      <span v-if="isDocField" class="fieldNotes" data-testid="custom-fields-form-field-notes-input">
        <span class="text-caption">
          <i18n-t
            keypath="customForms.messages.maximumInSizeOnlyOneImageTextZipOrSpreadsheetAllowedToUploadMultipleDocsZipFirstThenUpload">
            <template #size><strong>50MB</strong></template>
            <template #imageLimit>
              <strong>{{ $t('customForms.messages.oneImage') }}</strong>
            </template>
            <template #typeLimitText>
              <strong>{{ $t('global.basicTerms.text') }}</strong>
            </template>
            <template #typeLimitZip>
              <strong>{{ $t('global.basicTerms.zip') }}</strong>
            </template>
            <template #typeLimitSpreadsheet>
              <strong>{{ $t('global.basicTerms.spreadsheet') }}</strong>
            </template>
          </i18n-t>
        </span>
      </span>
      <span v-else-if="isMultiDocField" class="fieldNotes">
        <span class="text-caption">
          <i18n-t
            keypath="customForms.messages.maximumInSizePerFileOnlyImageTextZipSpreadsheetAllowed">
            <template #size><strong>50MB</strong></template>
            <template #count>
              <strong>10</strong>
            </template>
            <template #image>
              <strong>{{ $t('global.basicTerms.image') }}</strong>
            </template>
            <template #text>
              <strong>{{ $t('global.basicTerms.text') }}</strong>
            </template>
            <template #zip>
              <strong>{{ $t('global.basicTerms.zip') }}</strong>
            </template>
            <template #spreadsheet>
              <strong>{{ $t('global.basicTerms.spreadsheet') }}</strong>
            </template>
          </i18n-t>
        </span>
      </span>
      <custom-field-drop-down-choices-form
        v-if="hasDropDownValues"
        v-model="localField.dropDownValues"></custom-field-drop-down-choices-form>

      <div class="d-flex mt-6 flex-row justify-space-between" v-if="isNumberField || isTextField">
        <v-text-field
          class="mr-2"
          type="number"
          v-model.number="localField.minLengthOrValue"
          :rules="
            isTextField
              ? $validator.rules.optionalNumberBetween(0, maxSizeTextField(localField.type))
              : []
          "
          :label="
            isNumberField
              ? $t('customForms.labels.minimumValue')
              : $t('customForms.labels.minimumLength')
          "
          data-testid="custom-fields-form-minimum-value-input"
          @keydown.enter.stop></v-text-field>

        <v-text-field
          class="ml-2"
          type="number"
          v-model.number="localField.maxLengthOrValue"
          :rules="
            isTextField
              ? $validator.rules.optionalNumberBetween(0, maxSizeTextField(localField.type))
              : []
          "
          :label="
            isNumberField
              ? $t('customForms.labels.maximumValue')
              : $t('customForms.labels.maximumLength')
          "
          data-testid="custom-fields-form-maximum-value-input"
          @keydown.enter.stop></v-text-field>
      </div>

      <v-container class="my-6">
        <v-row>
          <v-switch
            v-model="localField.requiredForWarehouse"
            hide-details
            color="primary"
            class="mr-3 mt-0">
            <template #label>
              <span class="pr-1">
                {{ $t('customFields.forms.customFieldsForm.labels.requiredForWarehouse') }}
              </span>
              <help-icon-tooltip icon-style-class="grey darken-2">
                {{
                  $t(
                    'customFields.forms.customFieldsForm.tooltips.thisFieldIsMandatoryForWarehouseUsersToFillUpWhenSchedulingAnAppointment'
                  )
                }}
              </help-icon-tooltip>
            </template>
          </v-switch>
        </v-row>

        <v-row>
          <v-switch
            v-model="localField.hiddenFromCarrier"
            color="primary"
            class="mr-2 mt-0"
            hide-details>
            <template #label>
              <span class="pr-1">Hide this field on carrier scheduling</span>
              <help-icon-tooltip icon-style-class="grey darken-2">
                {{
                  $t(
                    'customFields.forms.customFieldsForm.tooltips.thisFieldWillNotBeVisibleToCarriersWhenSchedulingAnAppointment'
                  )
                }}
              </help-icon-tooltip>
            </template>
          </v-switch>
        </v-row>

        <v-row v-if="!localField.hiddenFromCarrier">
          <v-switch
            v-model="localField.requiredForCarrier"
            color="primary"
            class="mr-2 mt-0"
            hide-details>
            <template #label>
              <span class="pr-1">
                {{ $t('customFields.forms.customFieldsForm.labels.requiredForCarriers') }}
              </span>
              <help-icon-tooltip icon-style-class="grey darken-2">
                {{
                  $t(
                    'customFields.forms.customFieldsForm.tooltips.thisFieldIsMandatoryForCarrierUsersToFillUpWhenSchedulingAnAppointment'
                  )
                }}
              </help-icon-tooltip>
            </template>
          </v-switch>
        </v-row>
      </v-container>

      <action-group
        @cancel="$emit('close')"
        class="pr-0"
        confirm-icon="upload"
        :confirm-label="$t('global.buttons.save')"
        cancel-color="error"
        @confirm="submit"></action-group>
    </v-container>
  </v-form>
</template>

<script>
import { FIELDS_WITHOUT_PLACEHOLDER } from '@nova/core';

/**
 * Custom field form
 * @displayName Custom Field Form
 */
export default {
  props: {
    /**
     * Custom Field
     */
    field: {
      type: Object,
      requiredForCarrier: false,
      requiredForWarehouse: false,
      hiddenFromCarrier: false,
      label: String,
      description: String,
      dropDownValues: [],
      minLengthOrValue: null,
      maxLengthOrValue: null
    },
    fieldTypeOptions: {
      value: String,
      text: String
    },
    warehouse: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      localField: {},
      typesWithoutPlaceholder: Object.freeze(FIELDS_WITHOUT_PLACEHOLDER)
    };
  },
  computed: {
    isDocField() {
      return this.localField.type === this.novaCore.CustomFieldType.Document;
    },
    isMultiDocField() {
      return this.localField.type === this.novaCore.CustomFieldType.MultiDocument;
    },
    isNumberField() {
      return this.localField.type === this.novaCore.CustomFieldType.Number;
    },
    isTextField() {
      return (
        this.localField.type === this.novaCore.CustomFieldType.String ||
        this.localField.type === this.novaCore.CustomFieldType.BigString
      );
    },
    hasDropDownValues() {
      return (
        this.localField.type &&
        (this.localField.type === this.novaCore.CustomFieldType.DropDown ||
          this.localField.type === this.novaCore.CustomFieldType.DropDownMultiSelect ||
          this.localField.type === this.novaCore.CustomFieldType.ComboBox)
      );
    }
  },
  methods: {
    /**
     * Submit the form
     * @public
     */
    async submit() {
      if (this.hasDropDownValues && this.localField.dropDownValues.length < 1) {
        this.notify(
          this.$t(
            'customFields.forms.customFieldsForm.messages.atLeastOneOptionIsRequiredForDropdownFields'
          ),
          'warning'
        );
        return;
      }
      if (await this.$isFormValid(this.$refs.form)) {
        if (!this.hasDropDownValues) {
          // Reset template list when it is not dropdown
          this.localField.dropDownValues = [];
        }
        this.$emit('save', this.localField);
      }
    },
    maxSizeTextField(fieldType) {
      if (fieldType === this.novaCore.CustomFieldType.String) {
        return 255;
      }

      if (fieldType === this.novaCore.CustomFieldType.BigString) {
        return 1000;
      }
    }
  },
  watch: {
    'localField.hiddenFromCarrier'(value) {
      if (value) {
        this.localField.requiredForCarrier = false;
      }
    }
  },
  mounted() {
    this.localField = { ...this.field };

    if (!this.localField.dropDownValues) {
      // Make sure we have it initialized
      this.localField.dropDownValues = [];
    }
  }
};
</script>
